import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PersonalInfoStep from './components/PersonalInfoStep';
import AccountSecurityStep from './components/AccountSecurityStep';
import { authService } from '../../services/authService';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';
import { TERMS_VERSION } from '../../constants/legal';

const UserRegistration = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'common']);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    institution: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    receiveUpdates: false,
    availableAsReviewer: true,
    reviewerKeywords: []
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = [
    { id: 'personal', label: t('registration.personalInfo'), icon: 'User' },
    { id: 'security', label: t('registration.accountSecurity'), icon: 'ShieldCheck' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData?.firstName?.trim()) newErrors.firstName = t('registration.firstNameRequired');
      if (!formData?.lastName?.trim()) newErrors.lastName = t('registration.lastNameRequired');
      if (!formData?.email?.trim()) {
        newErrors.email = t('registration.emailRequired');
      } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/?.test(formData?.email?.trim())) {
        newErrors.email = t('registration.emailInvalid');
      }
      if (!formData?.country) newErrors.country = t('registration.countryPlaceholder');
      if (!formData?.institution?.trim()) newErrors.institution = t('registration.institutionPlaceholder');
    }

    if (step === 1) {
      if (!formData?.password) {
        newErrors.password = t('registration.passwordRequired');
      } else if (formData?.password?.length < 12) {
        newErrors.password = t('registration.passwordTooShort');
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9])/?.test(formData?.password)) {
        newErrors.password = t('registration.passwordTooShort');
      }
      if (!formData?.confirmPassword) {
        newErrors.confirmPassword = t('registration.confirmPasswordRequired');
      } else if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = t('registration.passwordMismatch');
      }
      if (!formData?.agreeToTerms) {
        newErrors.agreeToTerms = t('registration.termsRequired');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;

    if (currentStep === 0) {
      const email = formData?.email?.trim()?.toLowerCase();
      const { data } = await supabase.rpc('check_email_exists', { email_to_check: email });
      if (data) {
        setErrors({ email: t('registration.emailExists', { defaultValue: 'An account with this email already exists. Please sign in instead.' }) });
        return;
      }
    }

    if (currentStep < steps?.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    if (currentStep < steps?.length - 1) {
      setCurrentStep(currentStep + 1);
      return;
    }

    try {
      setIsSubmitting(true);
      setErrors({});

      const { data: authData, error: signUpError } = await authService?.signUp({
        email: formData?.email?.trim()?.toLowerCase(),
        password: formData?.password,
        firstName: formData?.firstName,
        lastName: formData?.lastName,
        country: formData?.country,
        institution: formData?.institution,
        receiveUpdates: formData?.receiveUpdates,
        availableAsReviewer: formData?.availableAsReviewer,
        reviewerKeywords: formData?.availableAsReviewer ? formData?.reviewerKeywords : [],
        termsAcceptedAt: new Date()?.toISOString(),
        termsVersion: TERMS_VERSION
      });

      if (signUpError) {
        setErrors({ form: signUpError?.message || t('registration.registrationError') });
        return;
      }

      if (!authData?.user) {
        throw new Error(t('registration.registrationError'));
      }

      if (authData?.user?.identities?.length === 0) {
        setErrors({ form: t('registration.emailExists', { defaultValue: 'An account with this email already exists. Please sign in instead.' }) });
        return;
      }

      navigate(`/check-email?email=${encodeURIComponent(formData?.email?.trim()?.toLowerCase())}`);
    } catch (err) {
      console.error('Registration error:', err);
      setErrors({ form: err?.message || t('registration.registrationError') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <PersonalInfoStep formData={formData} errors={errors} onChange={handleChange} />;
      case 1:
        return <AccountSecurityStep formData={formData} errors={errors} onChange={handleChange} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />

      <main className="relative overflow-hidden">
        {/* Blueprint grid backdrop */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.5] dark:opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(18,20,26,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(18,20,26,0.05) 1px, transparent 1px)',
            backgroundSize: '48px 48px'
          }}
        />

        <div className="relative pt-6 pb-16 px-4 md:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 border border-primary/20 mb-4">
                <Icon name="UserPlus" size={26} className="text-primary" />
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-foreground tracking-tight mb-2">
                {t('registration.title')}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground max-w-xl mx-auto">
                {t('registration.subtitle')}
              </p>
            </div>

            {/* Stepper */}
            <div className="flex items-center justify-center gap-3 mb-8">
              {steps?.map((step, i) => {
                const active = i === currentStep;
                const done = i < currentStep;
                return (
                  <React.Fragment key={step?.id}>
                    <div className="flex items-center gap-2">
                      <span className={`w-8 h-8 flex items-center justify-center text-xs font-bold border transition-colors ${
                        done ? 'bg-primary border-primary text-white'
                          : active ? 'bg-primary/10 border-primary text-primary '
                          : 'bg-transparent border-border text-muted-foreground'
                      }`}>
                        {done ? <Icon name="Check" size={14} /> : i + 1}
                      </span>
                      <span className={`text-xs font-medium hidden sm:inline ${active || done ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {step?.label}
                      </span>
                    </div>
                    {i < steps.length - 1 && <span className={`h-px w-10 ${done ? 'bg-primary' : 'bg-border'}`} />}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Card */}
            <div className="bg-card border border-border border-t-2 border-t-primary p-6 md:p-8">
              <form onSubmit={handleSubmit}>
                {errors?.form && (
                  <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-2">
                    <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-error">{errors?.form}</p>
                  </div>
                )}

                <div className="mb-8">
                  {renderStepContent()}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-border">
                  {currentStep > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={handleBack}
                      iconName="ArrowLeft"
                      iconPosition="left"
                      className="sm:w-auto w-full"
                    >
                      {t('common:actions.back')}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="default"
                    size="lg"
                    onClick={currentStep === steps?.length - 1 ? handleSubmit : handleNext}
                    loading={isSubmitting}
                    iconName={currentStep === steps?.length - 1 ? 'Check' : 'ArrowRight'}
                    iconPosition="right"
                    className="flex-1"
                  >
                    {currentStep === steps?.length - 1 ? t('auth:registration.submitButton') : t('common:actions.next')}
                  </Button>
                </div>
              </form>
            </div>

            <p className="mt-5 text-center text-sm text-muted-foreground">
              {t('auth:registration.alreadyHaveAccount')}{' '}
              <button
                onClick={() => navigate('/user-sign-in')}
                className="text-primary  hover:underline font-medium"
              >
                {t('auth:registration.signInLink')}
              </button>
            </p>

            {/* What happens next */}
            <div className="mt-6 border border-border bg-card/60 backdrop-blur-sm p-5">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={18} className="text-primary  flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">
                    {t('registration.whatNext.title', { defaultValue: 'What happens next?' })}
                  </h3>
                  <ul className="text-xs md:text-sm text-muted-foreground space-y-1.5">
                    <li>• {t('registration.whatNext.verify', { defaultValue: "You'll receive a verification email at your registered address" })}</li>
                    <li>• {t('registration.whatNext.activate', { defaultValue: 'Click the verification link to activate your account' })}</li>
                    <li>• {t('registration.whatNext.participate', { defaultValue: 'Once verified, you can sign in and start participating' })}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserRegistration;
